/**
 * Printer Module
 * Handles receipt printing with silent mode via QZ Tray.
 * Falls back to browser print dialog if QZ Tray is not available.
 *
 * QZ Tray is a free local service (https://qz.io) that allows
 * web apps to print directly to thermal printers without dialogs.
 *
 * Setup: Install QZ Tray on the POS computer → it runs in background.
 */

let qzAvailable: boolean | null = null;

/**
 * Check if QZ Tray is available (running on this machine).
 */
export async function isQzTrayAvailable(): Promise<boolean> {
  if (qzAvailable !== null) return qzAvailable;

  try {
    // QZ Tray exposes a WebSocket on localhost:8181
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);

    const response = await fetch('https://localhost:8181/api', {
      method: 'GET',
      signal: controller.signal,
    }).catch(() => null);

    clearTimeout(timeout);
    qzAvailable = response !== null && response.ok;
    return qzAvailable;
  } catch {
    qzAvailable = false;
    return false;
  }
}

/**
 * Get the default printer name via QZ Tray.
 */
export async function getDefaultPrinter(): Promise<string | null> {
  try {
    const response = await fetch('https://localhost:8181/api/printer/default');
    if (response.ok) {
      const data = await response.json();
      return data.printer || null;
    }
  } catch {
    // QZ Tray not available
  }
  return null;
}

/**
 * Print HTML content silently via QZ Tray.
 * If QZ Tray is not available, falls back to browser print dialog.
 */
export async function printReceipt(html: string): Promise<void> {
  const useQz = await isQzTrayAvailable();

  if (useQz) {
    await printViaQzTray(html);
  } else {
    printViaIframe(html);
  }
}

/**
 * Print via QZ Tray (silent, no dialog).
 */
async function printViaQzTray(html: string): Promise<void> {
  try {
    const response = await fetch('https://localhost:8181/api/print', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'html',
        data: html,
        options: {
          silent: true,
          copies: 1,
        },
      }),
    });

    if (!response.ok) {
      console.warn('[Printer] QZ Tray print failed, falling back to browser');
      printViaIframe(html);
    }
  } catch {
    console.warn('[Printer] QZ Tray not responding, falling back to browser');
    printViaIframe(html);
  }
}

/**
 * Print via hidden iframe (shows browser print dialog).
 * This is the fallback when QZ Tray is not installed.
 */
export function printViaIframe(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-10000px';
  iframe.style.left = '-10000px';
  iframe.style.width = '320px';
  iframe.style.height = '600px';
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (iframeDoc) {
    // Remove any auto-print scripts from the HTML
    const cleanHtml = html.replace(/<script>.*?<\/script>/gs, '');
    iframeDoc.open();
    iframeDoc.write(cleanHtml);
    iframeDoc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 250);
  }
}

/**
 * Get printer status info.
 */
export function getPrinterStatus(): { mode: 'qz-tray' | 'browser'; available: boolean } {
  return {
    mode: qzAvailable ? 'qz-tray' : 'browser',
    available: true,
  };
}
